import React, { useState, useMemo, useRef, useEffect } from 'react'
import PropTypes from 'prop-types'
import { Typography, Icon, Box, Stack, Tooltip, IconButton, CircularProgress, Grid } from '@mui/material'
import { getIconFromFileType, getColorFromFileType, formatSize, getDownloadPayload, getFolderTooltip, isFolder } from './FtpUtils'
import { NewTable, SORT, sortRows } from 'components/datatable/NewTable'
import moment from 'moment'
import i18n from 'simple-react-i18n'
import { useDispatch } from 'react-redux'
import ConfirmModal from 'components/modal/ConfirmModal'
import JobAction from 'import/actions/JobAction'
import FileAction from './FileAction'
import map from 'lodash/map'
import concat from 'lodash/concat'
import template from 'lodash/template'
import { FileDownload, DeleteOutline } from '@mui/icons-material'

const ActionFtp = ({ isFolderNode, item, onDownload, onDelete }) => (
    <Box display='flex' gap={0.5} justifyContent='flex-end'>
        {(!isFolderNode || item.depth > 0) && (
            <Tooltip title={i18n.download}>
                <IconButton size='small' onClick={(e) => onDownload(e, item)} color='secondary' sx={{ p: 0.5 }}>
                    <FileDownload fontSize='small' />
                </IconButton>
            </Tooltip>
        )}
        {(!isFolderNode || item.depth > 0) && (
            <Tooltip title={i18n.delete}>
                <IconButton size='small' onClick={(e) => onDelete(e, item)} color='error' sx={{ p: 0.5 }}>
                    <DeleteOutline fontSize='small' />
                </IconButton>
            </Tooltip>
        )}
    </Box>
)

ActionFtp.propTypes = {
    isFolderNode: PropTypes.bool.isRequired,
    item: PropTypes.object.isRequired,
    onDownload: PropTypes.func.isRequired,
    onDelete: PropTypes.func.isRequired,
}

const FtpListView = ({
    folders,
    files,
    visibleCount = (folders.length + files.length),
    onLoadMore,
    containerRef,
    onNavigate,
    onReload,
}) => {
    const dispatch = useDispatch()
    const [confirmItem, setConfirmItem] = useState(null)
    const [sort, setSort] = useState({ column: 'name', direction: SORT.ASC })
    const sentinelRef = useRef(null)

    const headers = [
        { key: 'fileType', value: '', width: 44 },
        { key: 'name', value: i18n.name },
        { key: 'size', value: i18n.size, width: 120 },
        { key: 'modifiedAt', value: i18n.date, width: 150 },
        { key: 'nullValueActions', value: '', width: 80, noSort: true, align: 'right' },
    ]

    const handleDownload = (e, item) => {
        e.stopPropagation()
        const isFolderNode = isFolder(item)
        if (!isFolderNode) {
            const { downloadName, relativePath } = getDownloadPayload(item)
            dispatch(JobAction.downloadFile(downloadName, relativePath))
        } else {
            dispatch(FileAction.zipDir(item.path, (zipPath) => {
                dispatch(JobAction.downloadFile(`${item.label}.zip`, zipPath))
            }))
        }
    }

    const handleDeleteClick = (e, item) => {
        e.stopPropagation()
        setConfirmItem(item)
    }

    const handleConfirmValidate = () => {
        if (!confirmItem) return
        const isFolderNode = isFolder(confirmItem)
        const action = isFolderNode
            ? FileAction.deleteDir(confirmItem.path, onReload)
            : FileAction.deleteFile(confirmItem.path, onReload)

        dispatch(action)
        setConfirmItem(null)
    }
    const FOLDER = 'folder'

    const createRow = (item) => {
        const isFolderNode = isFolder(item)
        const iconComponent = isFolderNode ? FOLDER : getIconFromFileType(item.fileType)
        const iconColor = getColorFromFileType(isFolderNode ? FOLDER : item.fileType)
        const name = item.label || item.id || ''
        const tooltipTitle = isFolderNode ? getFolderTooltip(item) : ''

        const iconEl = (
            <Icon fontSize='small' sx={{ color: iconColor }}>
                {iconComponent}
            </Icon>
        )
        const FILE = 'file'
        const extensionValue = isFolderNode
            ? 'aaa_folder'
            : (item.fileType && item.fileType !== FILE ? item.fileType.toLowerCase() : (item.label?.split('.').pop() || 'zzz_file').toLowerCase())

        return {
            fileType: {
                value: isFolderNode ? (
                    <Tooltip title={tooltipTitle || ''} placement='top' arrow>
                        {iconEl}
                    </Tooltip>
                ) : iconEl,
                sortValue: extensionValue,
            },
            name: {
                value: (
                    <Typography variant='body2'>
                        {name}
                    </Typography>
                ),
                sortValue: name.toLowerCase(),
                onClick: isFolderNode ? () => onNavigate(item) : undefined,
            },
            size: {
                value: (
                    <Typography variant='body2' >
                        {isFolderNode ? '—' : formatSize(item.size)}
                    </Typography>
                ),
                sortValue: item.size || 0,
            },
            modifiedAt: {
                value: (
                    <Typography variant='body2'>
                        {item.modifiedAt ? moment(item.modifiedAt).format('DD/MM/YYYY HH:mm') : '—'}
                    </Typography>
                ),
                sortValue: item.modifiedAt ? moment(item.modifiedAt).valueOf() : 0,
            },
            nullValueActions: (
                <ActionFtp
                    isFolderNode={isFolderNode}
                    item={item}
                    onDownload={handleDownload}
                    onDelete={handleDeleteClick}
                />
            ),
        }
    }

    const allRows = useMemo(() => {
        return concat(
            map(folders, item => createRow(item)),
            map(files, item => createRow(item))
        )
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [folders, files])

    const sortedRows = useMemo(() => {
        return sortRows(allRows, sort)
    }, [allRows, sort])

    const displayedRows = useMemo(() => {
        return sortedRows.slice(0, visibleCount)
    }, [sortedRows, visibleCount])

    const hasMore = displayedRows.length < sortedRows.length

    useEffect(() => {
        const sentinel = sentinelRef.current
        if (sentinel && hasMore && onLoadMore) {
            const observer = new IntersectionObserver(
                (entries) => {
                    if (entries[0]?.isIntersecting) {
                        onLoadMore()
                    }
                },
                {
                    root: containerRef?.current || null,
                    rootMargin: '250px',
                    threshold: 0,
                },
            )

            observer.observe(sentinel)
            return () => observer.disconnect()
        }
        return undefined
    }, [hasMore, onLoadMore, containerRef, visibleCount])

    const confirmSubTitle = confirmItem
        ? template(i18n.ftpConfirmDeleteMessage)({ name: confirmItem.label || confirmItem.id })
        : ''

    return (
        <Stack>
            <NewTable
                rows={displayedRows}
                headers={headers}
                defaultSort={sort}
                onSort={(newSort) => setSort(newSort)}
                rowsPerPageOptions={[-1]}
            />
            {hasMore && (
                <Grid
                    ref={sentinelRef}
                    container
                    direction='column'
                    alignItems='center'
                    justifyContent='center'
                    py={2.5}
                    spacing={1}
                >
                    <CircularProgress size={24} color='primary' />
                    <Typography variant='caption' sx={{ color: 'text.secondary', fontWeight: 500 }}>
                        {template(i18n.ftpShowingCount)({
                            current: displayedRows.length,
                            total: sortedRows.length,
                        })}
                    </Typography>
                </Grid>
            )}
            {!!confirmItem && (
                <ConfirmModal
                    isOpen={!!confirmItem}
                    title={i18n.ftpConfirmDeleteTitle}
                    subTitle={confirmSubTitle}
                    onValidate={handleConfirmValidate}
                    onClose={() => setConfirmItem(null)}
                />
            )}
        </Stack>
    )
}

FtpListView.propTypes = {
    folders: PropTypes.array.isRequired,
    files: PropTypes.array.isRequired,
    visibleCount: PropTypes.number,
    onLoadMore: PropTypes.func,
    containerRef: PropTypes.object,
    onNavigate: PropTypes.func.isRequired,
    onReload: PropTypes.func.isRequired,
}

export default FtpListView
